/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import 'rc-slider/assets/index.css'
import 'rc-tooltip/assets/bootstrap.css'
import Slider from 'rc-slider'

import { updateSelectedFilters } from 'store/actions/filters'
import Button from 'components/ui/Button'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import CustomCheckbox from 'components/ui/CustomCheckbox'
import CustomRadioButton from 'components/ui/CustomRadioButton'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import {
  DEFAULT_PROJECT_END_YEAR_MAX,
  DEFAULT_PROJECT_END_YEAR_MIN,
  DEFAULT_PROJECT_START_YEAR_MAX,
  DEFAULT_PROJECT_START_YEAR_MIN,
  SELECTED_FILTER_MAX_LINES
} from 'store/constants/constants'
import Dotdotdot from 'react-dotdotdot'

const { createSliderWithTooltip } = Slider
const StartRange = createSliderWithTooltip(Slider.Range)
const EndRange = createSliderWithTooltip(Slider.Range)

const FilterSection = styled.div`
  margin-bottom: 15px;
`
const StyledStartRange = styled(StartRange)`
  user-select: none;
`
const StyledEndRange = styled(EndRange)`
  user-select: none;
`
const Input = styled.input`
  margin-right: 5px;
`
const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #4a4a4a;
`
const SubHeader = styled.h4`
  font-size: 16px;
  font-weight: normal;
  margin-bottom: 10px;
  font-family: 'Open Sans';
`

function ProjectDetails({ type, filterData, values, title, onClose, showFilter, customTop }) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  const filterObject = filters.selectedFilters[currentSearchable][type]

  return (
    <PureProjectDetails
      type={type}
      values={values}
      filterData={filterData}
      title={title}
      onClose={onClose}
      showFilter={showFilter}
      customTop={customTop}
      filterObject={filterObject}
    />
  )
}

const PureProjectDetails = React.memo(function PureProjectDetails({
  type,
  filterData,
  values,
  title,
  onClose,
  showFilter,
  customTop,
  filterObject
}) {
  const selectedOption = filterObject.status

  let checkboxStartSelected = filterObject.project_details.without_start
  if (!checkboxStartSelected) checkboxStartSelected = false

  let checkboxEndSelected = filterObject.project_details.without_end
  if (!checkboxEndSelected) checkboxEndSelected = false
  //const defaultStartYears = [DEFAULT_PROJECT_START_YEAR_MIN, DEFAULT_PROJECT_START_YEAR_MAX]
  //const defaultEndYears = [DEFAULT_PROJECT_END_YEAR_MIN, DEFAULT_PROJECT_END_YEAR_MAX]
  const [defaultStartYears, setDefaultStartYears] = useState([
    DEFAULT_PROJECT_START_YEAR_MIN,
    DEFAULT_PROJECT_START_YEAR_MAX
  ])
  const [defaultEndYears, setDefaultEndYears] = useState([
    DEFAULT_PROJECT_END_YEAR_MIN,
    DEFAULT_PROJECT_END_YEAR_MAX
  ])
  let selectedStartYears = defaultStartYears
  let selectedEndYears = defaultEndYears

  if (filterObject.project_details.start.length > 0) {
    selectedStartYears = [
      filterObject.project_details.start[0],
      filterObject.project_details.start[1]
    ]
  }
  if (filterObject.project_details.end.length > 0) {
    selectedEndYears = [filterObject.project_details.end[0], filterObject.project_details.end[1]]
  }
  const currentYear = new Date().getFullYear()
  const dispatch = useDispatch()

  const [sliderStartYearRange, setSliderStartYearRange] = useState(selectedStartYears)
  const [sliderEndYearRange, setSliderEndYearRange] = useState(selectedEndYears)

  // Get the max and min budgets from database
  useEffect(() => {
    async function getDateRange() {
      const response = await fetch('/api/search/projects/date-range/')
      const data = await response.json()
      const start_date_range = [data.min_start_date, data.max_start_date]
      const end_date_range = [data.min_end_date, data.max_end_date]
      setDefaultStartYears(start_date_range)
      setDefaultEndYears(end_date_range)
      selectedStartYears = defaultStartYears
      selectedEndYears = defaultEndYears
    }

    getDateRange()
  }, [])

  //AKN-END

  function handleStartYearSliderLimits(value) {
    let valueChanged = false
    if (value[0] >= sliderEndYearRange[0]) {
      setSliderEndYearRange(prevVal => [value[0], prevVal[1]])
    }
    if (value[1] >= sliderEndYearRange[1]) {
      if (selectedOption === 'closed' && value[1] > currentYear) {
        setSliderEndYearRange(prevVal => [prevVal[0], currentYear])
        setSliderStartYearRange(prevVal => [prevVal[0], currentYear])
        valueChanged = true
      } else {
        setSliderEndYearRange(prevVal => [prevVal[0], value[1]])
      }
    }
    return valueChanged
  }

  function handleEndYearSliderLimits(value) {
    if (selectedOption === 'ongoing' && value[1] < currentYear) {
      setSliderEndYearRange([value[0], currentYear])
    } else if (selectedOption === 'closed' && value[1] > currentYear) {
      setSliderEndYearRange([value[0], currentYear])
    } else {
      if (value[0] <= sliderStartYearRange[0]) {
        setSliderStartYearRange(prevVal => [value[0], prevVal[1]])
      }
      if (value[1] <= sliderStartYearRange[1]) {
        setSliderStartYearRange(prevVal => [prevVal[0], value[1]])
      }
    }
  }

  function handleStartSliderChangeAfter() {
    const projectDetails = {
      start: sliderStartYearRange,
      without_start: checkboxStartSelected,
      end: sliderEndYearRange,
      without_end: checkboxEndSelected
    }
    const data = {
      filterType: type,
      filterId: 'project_details',
      filterValue: projectDetails
    }
    dispatch(updateSelectedFilters(data))
  }

  function handleEndSliderChangeAfter() {
    const projectDetails = {
      start: sliderStartYearRange,
      without_start: checkboxStartSelected,
      end: sliderEndYearRange,
      without_end: checkboxEndSelected
    }
    const data = {
      filterType: type,
      filterId: 'project_details',
      filterValue: projectDetails
    }
    dispatch(updateSelectedFilters(data))
  }

  function handleStartSliderChange(value) {
    if (!handleStartYearSliderLimits(value)) {
      setSliderStartYearRange(value)
    }
  }

  function handleEndSliderChange(value) {
    setSliderEndYearRange(value)
    handleEndYearSliderLimits(value)
  }

  function updateSliders(sliderStartYearValue, sliderEndYearValue) {
    setSliderStartYearRange(sliderStartYearValue)
    setSliderEndYearRange(sliderEndYearValue)
    const projectDetails = {
      start: sliderStartYearValue,
      without_start: checkboxStartSelected,
      end: sliderEndYearValue,
      without_end: checkboxEndSelected
    }
    const slidersData = {
      filterType: type,
      filterId: 'project_details',
      filterValue: projectDetails
    }
    dispatch(updateSelectedFilters(slidersData))
  }

  function handleSliderStatusChange(status, startRange, endRange) {
    let sliderStartYearValue = startRange
    let sliderEndYearValue = endRange
    if (status === 'ongoing' && endRange[1] < currentYear) {
      sliderEndYearValue = [endRange[0], currentYear]
    } else if (status === 'closed' && endRange[1] > currentYear) {
      sliderEndYearValue = [endRange[0], currentYear]
      if (startRange[1] >= endRange[1]) {
        sliderStartYearValue = [startRange[0], currentYear]
      }
    }
    updateSliders(sliderStartYearValue, sliderEndYearValue)
  }

  function handleOptionChange(changeEvent) {
    const data = {
      filterType: type,
      filterId: 'status',
      filterValue: changeEvent.target.value
    }
    dispatch(updateSelectedFilters(data))
    handleSliderStatusChange(changeEvent.target.value, selectedStartYears, selectedEndYears)
  }

  function handleSliderReset(startRange, endRange) {
    let sliderStartYearValue = startRange
    let sliderEndYearValue = endRange
    if (startRange[0] === defaultStartYears[0]) {
      sliderEndYearValue = [defaultEndYears[0], sliderEndYearValue[1]]
    }

    if (startRange[1] === defaultStartYears[1]) {
      sliderEndYearValue = [sliderEndYearValue[0], defaultEndYears[1]]
    }

    if (endRange[0] === defaultEndYears[0]) {
      sliderStartYearValue = [defaultStartYears[0], sliderStartYearValue[1]]
    }

    if (endRange[1] === defaultEndYears[1]) {
      sliderStartYearValue = [sliderStartYearValue[0], defaultStartYears[1]]
    }
    updateSliders(sliderStartYearValue, sliderEndYearValue)
  }

  function handleCheckboxStartChange() {
    const startChecked = !checkboxStartSelected

    const projectDetails = {
      start: selectedStartYears,
      without_start: startChecked,
      end: selectedEndYears,
      without_end: checkboxEndSelected
    }

    const data = {
      filterType: type,
      filterId: 'project_details',
      filterValue: projectDetails
    }
    dispatch(updateSelectedFilters(data))
  }

  function handleCheckboxEndChange() {
    const endChecked = !checkboxEndSelected

    const projectDetails = {
      start: selectedStartYears,
      without_start: checkboxStartSelected,
      end: selectedEndYears,
      without_end: endChecked
    }

    const data = {
      filterType: type,
      filterId: 'project_details',
      filterValue: projectDetails
    }
    dispatch(updateSelectedFilters(data))
  }

  function handleFilterRemove(filterId) {
    let resultingValue = null
    let filter = filterId
    if (filterId === 'project_details.start') {
      resultingValue = {
        start: defaultStartYears,
        without_start: checkboxStartSelected,
        end: sliderEndYearRange,
        without_end: checkboxEndSelected
      }
      filter = 'project_details'
      handleSliderReset(defaultStartYears, sliderEndYearRange)
    }
    if (filterId === 'project_details.end') {
      if (selectedOption === 'closed') {
        const statusData = {
          filterType: type,
          filterId: 'status',
          filterValue: null
        }
        dispatch(updateSelectedFilters(statusData))
      }
      resultingValue = {
        start: sliderStartYearRange,
        without_start: checkboxStartSelected,
        end: defaultEndYears,
        without_end: checkboxEndSelected
      }
      filter = 'project_details'
      handleSliderReset(sliderStartYearRange, defaultEndYears)
    }
    if (filterId === 'project_details.without_start') {
      resultingValue = {
        start: selectedStartYears,
        without_start: false,
        end: selectedEndYears,
        without_end: checkboxEndSelected
      }
      filter = 'project_details'
      const data = {
        filterType: type,
        filterId: filter,
        filterValue: resultingValue
      }
      dispatch(updateSelectedFilters(data))
    }
    if (filterId === 'project_details.without_end') {
      resultingValue = {
        start: selectedStartYears,
        without_start: checkboxStartSelected,
        end: selectedEndYears,
        without_end: false
      }
      filter = 'project_details'
      const data = {
        filterType: type,
        filterId: filter,
        filterValue: resultingValue
      }
      dispatch(updateSelectedFilters(data))
    }
    if (filterId === 'status') {
      const statusData = {
        filterType: type,
        filterId: 'status',
        filterValue: null
      }
      dispatch(updateSelectedFilters(statusData))
    }
  }

  function getStatusOptionLabel(option) {
    return t`Only ${option} projects`
  }

  function renderSelectedValues() {
    return values.map(value => {
      if (filterObject.project_details) {
        let filterValue = filterObject[value]
        if (value === 'project_details.start') {
          filterValue = filterObject.project_details
          if (
            filterValue.start.length &&
            (filterValue.start[0] !== defaultStartYears[0] ||
              filterValue.start[1] !== defaultStartYears[1])
          ) {
            const selectedFilterText = `Start year from: ${filterValue.start[0]} to: ${filterValue.start[1]}`
            return (
              <SelectedFilterContainer
                className="selected-filters"
                key={`start-${filterValue.start[0]}`}
              >
                <SelectedFilter isCapitalized>
                  <FilterText title={selectedFilterText}>
                    <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{selectedFilterText}</Dotdotdot>
                  </FilterText>
                  <RemoveIcon className="far fa-times" onClick={() => handleFilterRemove(value)} />
                </SelectedFilter>
              </SelectedFilterContainer>
            )
          }
          return false
        }
        if (value === 'project_details.end') {
          filterValue = filterObject.project_details
          if (
            filterValue.end.length &&
            (filterValue.end[0] !== defaultEndYears[0] || filterValue.end[1] !== defaultEndYears[1])
          ) {
            const selectedFilterText = `End year from: ${filterValue.end[0]} to: ${filterValue.end[1]}`
            return (
              <SelectedFilterContainer
                className="selected-filters"
                key={`end-${filterValue.end[0]}`}
              >
                <SelectedFilter isCapitalized>
                  <FilterText title={selectedFilterText}>
                    <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{selectedFilterText}</Dotdotdot>
                  </FilterText>
                  <RemoveIcon className="far fa-times" onClick={() => handleFilterRemove(value)} />
                </SelectedFilter>
              </SelectedFilterContainer>
            )
          }
          return false
        }
        if (value === 'project_details.without_start') {
          filterValue = filterObject.project_details.without_start
          if (filterValue) {
            const selectedFilterText =
              filterValue === true ? 'Include projects without known start year' : filterValue
            return (
              <SelectedFilterContainer className="selected-filters" key="without_start">
                <SelectedFilter isCapitalized>
                  <FilterText title={selectedFilterText}>
                    <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{selectedFilterText}</Dotdotdot>
                  </FilterText>
                  <RemoveIcon className="far fa-times" onClick={() => handleFilterRemove(value)} />
                </SelectedFilter>
              </SelectedFilterContainer>
            )
          }
          return false
        }
        if (value === 'project_details.without_end') {
          filterValue = filterObject.project_details.without_end
          if (filterValue) {
            const selectedFilterText =
              filterValue === true ? 'Include projects without known end year' : filterValue
            return (
              <SelectedFilterContainer className="selected-filters" key="without_end">
                <SelectedFilter isCapitalized>
                  <FilterText title={selectedFilterText}>
                    <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{selectedFilterText}</Dotdotdot>
                  </FilterText>
                  <RemoveIcon className="far fa-times" onClick={() => handleFilterRemove(value)} />
                </SelectedFilter>
              </SelectedFilterContainer>
            )
          }
          return false
        }
        if (filterValue) {
          const selectedFilterText = getStatusOptionLabel(filterValue)
          return (
            <SelectedFilterContainer className="selected-filters" key={filterValue}>
              <SelectedFilter isCapitalized>
                <FilterText title={selectedFilterText}>
                  <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{selectedFilterText}</Dotdotdot>
                </FilterText>
                <RemoveIcon className="far fa-times" onClick={() => handleFilterRemove(value)} />
              </SelectedFilter>
            </SelectedFilterContainer>
          )
        }
        return false
      }
      return false
    })
  }

  return (
    <>
      <SearchFilter open={showFilter} customTop={customTop}>
        <FilterTitle>{title}</FilterTitle>
        <FilterSection>
          {filterData.status.map((option, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index}>
              <CustomRadioButton>
                <Input
                  type="radio"
                  name="filter-option"
                  id={index}
                  value={option}
                  checked={selectedOption === option}
                  onChange={handleOptionChange}
                />
                <Label htmlFor={index}>{getStatusOptionLabel(option)}</Label>
              </CustomRadioButton>
            </div>
          ))}
        </FilterSection>
        <FilterSection>
          <SubHeader>{t`Start year: ${
            filterObject.project_details.start[0] || defaultStartYears[0]
          } - ${filterObject.project_details.start[1] || defaultStartYears[1]}`}</SubHeader>
          <StyledStartRange
            min={defaultStartYears[0]}
            max={defaultStartYears[1]}
            value={sliderStartYearRange}
            tipFormatter={value => `${value}`}
            onChange={handleStartSliderChange}
            onAfterChange={handleStartSliderChangeAfter}
            allowCross={false}
          />
        </FilterSection>
        <FilterSection>
          <CustomCheckbox>
            <Input
              type="checkbox"
              id="without_start"
              name="filter-checkbox"
              value={checkboxStartSelected}
              checked={checkboxStartSelected ? 'checked' : ''}
              onChange={handleCheckboxStartChange}
            />
            <Label htmlFor="without_start">{t`Include projects without known start year`}</Label>
          </CustomCheckbox>
        </FilterSection>
        <FilterSection>
          <SubHeader>{t`End year: ${filterObject.project_details.end[0] || defaultEndYears[0]} - 
           ${filterObject.project_details.end[1] || defaultEndYears[1]}`}</SubHeader>
          <StyledEndRange
            min={defaultEndYears[0]}
            max={defaultEndYears[1]}
            value={sliderEndYearRange}
            tipFormatter={value => `${value}`}
            onChange={handleEndSliderChange}
            onAfterChange={handleEndSliderChangeAfter}
            allowCross={false}
          />
        </FilterSection>
        <FilterSection>
          <CustomCheckbox>
            <Input
              type="checkbox"
              id="without_end"
              name="filter-checkbox"
              value={checkboxEndSelected}
              checked={checkboxEndSelected ? 'checked' : ''}
              onChange={handleCheckboxEndChange}
            />
            <Label htmlFor="without_end">{t`Include projects without known end year`}</Label>
          </CustomCheckbox>
        </FilterSection>
        <Button size="medium" onClick={onClose}>
          {t`Close`}
        </Button>
      </SearchFilter>
      <div>{renderSelectedValues()}</div>
    </>
  )
})

ProjectDetails.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.object,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string
}

PureProjectDetails.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.object,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  customTop: PropTypes.string,
  filterObject: PropTypes.object
}

export default ProjectDetails
